/**
 * What the Tasks section needs from a tasks backend, and nothing more.
 *
 * A task is any work someone owes -- a call to make, an invoice to send, a
 * contract to read -- not a dev tracker's issue. These types say it that way;
 * `sdk.ts` maps `@wtfalch/tasks`' issues onto them, and everything the
 * screens draw comes through here. A list is a team in the SDK; "My tasks"
 * is the one list that is not a team (id `MINE`).
 */

export const MINE = 'mine';

export const TASKS_OFF_MESSAGE = 'Tasks is not turned on for this company';

/** Where a task came from. The SDK's own four kinds. */
export type FromKind = 'chat.message' | 'chat.call' | 'mail.thread' | 'calendar.event';

export type Person = { id: string; name: string; me: boolean };

export type StatusGroup = 'backlog' | 'todo' | 'started' | 'done' | 'cancelled';

export type TaskStatus = { id: string; name: string; group: StatusGroup };

export type Task = {
  id: string;
  /** `FI-18`: what a team's tasks are called out loud. Personal tasks have one too. */
  key: string;
  title: string;
  notes: string | null;
  done: boolean;
  /** `YYYY-MM-DD`. */
  dueOn: string | null;
  /** `MINE` for a personal task, else the team's id. */
  listId: string;
  statusId: string;
  statusName: string;
  assignee: Person | null;
  from: { kind: FromKind; label: string } | null;
  /** Sub-tasks this one has, and how many are done. */
  steps: number;
  stepsDone: number;
};

export type TaskDetail = Task & {
  stepList: readonly Task[];
  attachments: readonly { id: string; name: string }[];
};

export type TaskList = {
  id: string;
  name: string;
  /** Empty for `MINE`, whose columns are the fixed personal ones. */
  members: readonly Person[];
  statuses: readonly TaskStatus[];
  open: number;
  done: number;
};

export type NewTask = {
  title: string;
  listId: string;
  dueOn?: string | null;
  from?: { kind: FromKind; sourceId: string; label: string };
};

export type TasksSource = {
  me(): Promise<Person>;
  lists(): Promise<readonly TaskList[]>;
  /** `MINE`: everything open that is yours. A team: every task in it, done too. */
  tasks(listId: string): Promise<readonly Task[]>;
  task(id: string): Promise<TaskDetail>;
  create(input: NewTask): Promise<Task>;
  setDone(task: Task, done: boolean): Promise<Task>;
  moveTo(id: string, statusId: string): Promise<Task>;
  addStep(parentId: string, title: string): Promise<Task>;
};

export const TITLE_MAX = 200;
export const NOTES_MAX = 20_000;

// C0/C1 controls except tab and newline, and the bidi override characters
// that let a title read in a different order than it is written.
function isUnsafe(code: number): boolean {
  return (
    (code < 0x20 && code !== 0x09 && code !== 0x0a) ||
    (code >= 0x7f && code <= 0x9f) ||
    (code >= 0x202a && code <= 0x202e) ||
    (code >= 0x2066 && code <= 0x2069)
  );
}

/** Text from another person or a service: a string, free of control and
 *  bidi-override characters, no longer than `max`. Anything else is empty. */
export function cleanText(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  return Array.from(value)
    .filter((ch) => !isUnsafe(ch.codePointAt(0) ?? 0))
    .join('')
    .slice(0, max);
}

/** A title somebody typed: one line, trimmed, not empty, not too long. */
export function parseTitle(
  input: string,
): { ok: true; title: string } | { ok: false; error: string } {
  const title = cleanText(input, TITLE_MAX + 1)
    .replace(/\s+/g, ' ')
    .trim();
  if (title === '') return { ok: false, error: 'A task needs a title.' };
  if (title.length > TITLE_MAX) {
    return { ok: false, error: `A title is at most ${TITLE_MAX} characters.` };
  }
  return { ok: true, title };
}
